import {
  Grid2,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  FormHelperText,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Box,
  Divider,
} from '@mui/material';
import ButtonComponent from 'components/ButtonComponent';
import ImageUploaderComponent from 'components/ImageUploaderComponent';
import ModalComponent from 'components/ModalComponent';
import TextAreaComponentV2 from 'components/TextAreaComponentV2';
import TextFieldComponent from 'components/TextFieldComponent';
import { AppContext } from 'context/AppContextProvider';
import dayjs from 'dayjs';
import { useContext, useEffect, useState, useRef, useCallback } from 'react';
import {
  useCreateSchoolBoardAsync,
  useUpdateSchoolBoardAsync,
} from 'services/SchoolBoard/hooks/useMutation';
import {
  ISchoolBoardCreateProps,
  ISchoolBoardProps,
  ISchoolBoardUpdateProps,
} from 'services/SchoolBoard/types';
import {
  transformHtmlToPlainText,
  transformPlainTextToHtml,
} from 'utils/TransformUtils';
import { useGetAllSchoolClassByLicenseAsync } from 'services/SchoolClass/hooks/useMutation/index';
import { useGetAllStudentByLicenseAsync } from 'services/StudentService/hooks/useQuery/index';
import { useGetAllResponsibleByLicenseAsync } from 'services/ResponsibleService/hooks/useQuery/index';
import { SchoolBoardFormSchema } from '../../../services/SchoolBoard/schemas';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import DatePickerComponent from '../../../components/DatePickerComponent';
import zod from 'zod';

interface ISchoolBoardEditModalProps {
  isOpen: boolean;
  schoolBoardToEdit?: ISchoolBoardProps;
  handleSaveForm?: () => void;
  handleClose: () => void;
}

export type SchoolBoardFormInput = zod.infer<typeof SchoolBoardFormSchema>;

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: 48 * 4.5 + 8,
      width: 250,
    },
  },
};

const SchoolBoardEditModal = ({
  isOpen,
  handleClose,
  schoolBoardToEdit,
  handleSaveForm,
}: ISchoolBoardEditModalProps) => {
  const { showSnackbar, setIsShowLoadingComponent } = useContext(AppContext);
  const [selectedImage, setSelectedImage] = useState<Blob | string>();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [wasSubmitted, setWasSubmitted] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { control, reset, handleSubmit, setValue, watch } =
    useForm<SchoolBoardFormInput>({
      resolver: zodResolver(SchoolBoardFormSchema),
      defaultValues: {
        name: '',
        description: '',
        initialDate: '',
        validUntil: '',
        image: '',
        classId: [],
        studentId: [],
        responsibleId: [],
      },
    });

  const currentStudents = watch('studentId') || [];
  const currentClasses = watch('classId') || [];
  const currentResponsibles = watch('responsibleId') || [];

  const { data: classesData, mutate: fetchClasses } =
    useGetAllSchoolClassByLicenseAsync();
  const { data: studentsData } = useGetAllStudentByLicenseAsync(
    { pageNumber: 1, pageSize: 999 },
    { name: '' },
  );
  const { data: responsiblesData } = useGetAllResponsibleByLicenseAsync(
    { pageNumber: 1, pageSize: 999 },
    { name: '' },
  );

  const {
    mutateAsync: mutateCreateSchoolBoard,
    isLoading: isLoadingCreateSchoolBoard,
  } = useCreateSchoolBoardAsync();
  const {
    mutateAsync: mutateUpdateSchoolBoard,
    isLoading: isLoadingUpdateSchoolBoard,
  } = useUpdateSchoolBoardAsync();

  useEffect(() => {
    if (currentResponsibles.length > 0 && responsiblesData) {
      const selectedResps = responsiblesData.filter((r: any) =>
        currentResponsibles.includes(r.id),
      );
      const linkedStudentIds: number[] = selectedResps
        .flatMap((r: any) => r.students?.map((s: any) => s.id) || []);
      if (linkedStudentIds.length > 0) {
        const combinedIds = Array.from(new Set([...currentStudents, ...linkedStudentIds]));
        if (combinedIds.length !== currentStudents.length) {
          setValue('studentId', combinedIds);
        }
      }
    }
  }, [currentResponsibles, responsiblesData, setValue, currentStudents]);

  const handleBase64Conversion = (image: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(image);
    });
  };

  const onRemoveImage = useCallback(() => {
    setSelectedImage(undefined);
    setPreviewUrl(null);
    setValue('image', '');
  }, [setValue]);

  const handleSave = async (data: SchoolBoardFormInput) => {
    setWasSubmitted(true);
    if (!selectedImage) {
      showSnackbar('A imagem é obrigatória.', 'error');
      return;
    }

    setIsShowLoadingComponent(true);
    try {
      const payload = { ...data };
      if (selectedImage instanceof Blob) {
        payload.image = await handleBase64Conversion(selectedImage);
      }
      payload.description = transformHtmlToPlainText(payload.description || '');

      if (payload.id) {
        await mutateUpdateSchoolBoard({
          id: payload.id,
          data: payload as ISchoolBoardUpdateProps,
        });
        showSnackbar('Recado atualizado com sucesso!', 'success');
      } else {
        await mutateCreateSchoolBoard(payload as ISchoolBoardCreateProps);
        showSnackbar('Recado criado com sucesso!', 'success');
      }

      onRemoveImage();
      reset();
      if (handleSaveForm) handleSaveForm();
      handleClose();
    } catch (error: any) {
      showSnackbar(error.message || 'Erro ao salvar recado', 'error');
    } finally {
      setIsShowLoadingComponent(false);
    }
  };

  useEffect(() => {
    if (isOpen) fetchClasses(undefined);
  }, [isOpen, fetchClasses]);

  useEffect(() => {
    if (schoolBoardToEdit && isOpen) {
      reset({
        ...schoolBoardToEdit,
        description: schoolBoardToEdit.description || '',
        initialDate: schoolBoardToEdit.initialDate
          ? dayjs(schoolBoardToEdit.initialDate).toISOString()
          : '',
        validUntil: schoolBoardToEdit.validUntil
          ? dayjs(schoolBoardToEdit.validUntil).toISOString()
          : '',

        classId:
          schoolBoardToEdit.classId ||
          (schoolBoardToEdit as any).ClassId ||
          schoolBoardToEdit.class?.map((c: any) => c.id) ||
          [],

        studentId:
          schoolBoardToEdit.studentId ||
          (schoolBoardToEdit as any).StudentId ||
          schoolBoardToEdit.student?.map((s: any) => s.id) ||
          [],

        responsibleId:
          schoolBoardToEdit.responsibleId ||
          (schoolBoardToEdit as any).ResponsibleId ||
          schoolBoardToEdit.responsible?.map((r: any) => r.id) ||
          [],
      });
      setPreviewUrl(schoolBoardToEdit.image || null);
      setSelectedImage(schoolBoardToEdit.image || undefined);
    }
  }, [schoolBoardToEdit, reset, isOpen, classesData, studentsData, responsiblesData]);

  return (
    <ModalComponent
      title={schoolBoardToEdit ? 'Atualizar Recado' : 'Novo Recado'}
      onClose={handleClose}
      isOpen={isOpen}
    >
      <Grid2 container spacing={2}>
        <Grid2 size={12}>
          <Controller
            name="name"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextFieldComponent
                label="Título do Recado"
                onChangeText={field.onChange}
                errorMessage={error?.message}
                value={field.value}
              />
            )}
          />
        </Grid2>

        <Grid2 size={12}>
          <Box
            sx={{
              border: '1px dashed #ccc',
              borderRadius: 2,
              p: 1,
              textAlign: 'center',
            }}
          >
            {!previewUrl ? (
              <ImageUploaderComponent
                onDrop={(files) => {
                  const blob = new Blob([files[0]], { type: files[0].type });
                  setSelectedImage(blob);
                  setPreviewUrl(URL.createObjectURL(blob));
                  setValue('image', 'valid');
                }}
              />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <img
                  src={previewUrl}
                  alt="Preview"
                  style={{ maxHeight: 150, borderRadius: 8 }}
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <ButtonComponent
                    variant="outlined"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Alterar
                  </ButtonComponent>
                  <ButtonComponent
                    variant="outlined"
                    color="error"
                    onClick={onRemoveImage}
                  >
                    Remover
                  </ButtonComponent>
                </Box>
              </Box>
            )}
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  const blob = new Blob([file], { type: file.type });
                  setSelectedImage(blob);
                  setPreviewUrl(URL.createObjectURL(blob));
                  setValue('image', 'valid');
                }
              }}
            />
          </Box>
          {wasSubmitted && !selectedImage && (
            <FormHelperText error sx={{ textAlign: 'center' }}>
              Imagem obrigatória
            </FormHelperText>
          )}
        </Grid2>

        <Grid2 size={4}>
          <Controller
            name="classId"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel>Turmas</InputLabel>
                <Select
                  multiple
                  value={field.value || []}
                  onChange={(e) => {
                    const val = e.target.value as any[];
                    const totalClasses = classesData?.length ?? 0;
                    if (val.includes('all')) {
                      field.onChange(
                        currentClasses.length === totalClasses
                          ? []
                          : classesData?.map((c: any) => c.id),
                      );
                    } else field.onChange(val);
                  }}
                  input={<OutlinedInput label="Turmas" />}
                  renderValue={(selected) =>
                    classesData
                      ?.filter((c: any) => selected.includes(c.id))
                      .map((c: any) => c.name)
                      .join(', ')
                  }
                  MenuProps={MenuProps}
                >
                  <MenuItem value="all">
                    <Checkbox
                      checked={
                        (classesData?.length ?? 0) > 0 &&
                        currentClasses.length === (classesData?.length ?? 0)
                      }
                      indeterminate={
                        currentClasses.length > 0 &&
                        currentClasses.length < (classesData?.length ?? 0)
                      }
                    />
                    <ListItemText primary="Selecionar Todas" />
                  </MenuItem>
                  <Divider />
                  {classesData?.map((item: any) => (
                    <MenuItem key={item.id} value={item.id}>
                      <Checkbox checked={field.value.includes(item.id)} />
                      <ListItemText primary={item.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </Grid2>

        <Grid2 size={4}>
          <Controller
            name="studentId"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel>Alunos</InputLabel>
                <Select
                  multiple
                  value={field.value || []}
                  onChange={(e) => {
                    const val = e.target.value as any[];
                    const totalStudents = studentsData?.length ?? 0;
                    if (val.includes('all')) {
                      field.onChange(
                        currentStudents.length === totalStudents
                          ? []
                          : studentsData?.map((s: any) => s.id),
                      );
                    } else field.onChange(val);
                  }}
                  input={<OutlinedInput label="Alunos" />}
                  renderValue={(selected) =>
                    studentsData
                      ?.filter((s: any) => selected.includes(s.id))
                      .map((s: any) => s.name)
                      .join(', ')
                  }
                  MenuProps={MenuProps}
                >
                  <MenuItem value="all">
                    <Checkbox
                      checked={
                        (studentsData?.length ?? 0) > 0 &&
                        currentStudents.length === (studentsData?.length ?? 0)
                      }
                      indeterminate={
                        currentStudents.length > 0 &&
                        currentStudents.length < (studentsData?.length ?? 0)
                      }
                    />
                    <ListItemText primary="Selecionar Todos" />
                  </MenuItem>
                  <Divider />
                  {studentsData?.map((item: any) => (
                    <MenuItem key={item.id} value={item.id}>
                      <Checkbox checked={field.value.includes(item.id)} />
                      <ListItemText primary={item.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </Grid2>

        <Grid2 size={4}>
          <Controller
            name="responsibleId"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel>Responsáveis</InputLabel>
                <Select
                  multiple
                  value={field.value || []}
                  onChange={(e) => {
                    const val = e.target.value as any[];
                    const totalResponsibles = responsiblesData?.length ?? 0;
                    if (val.includes('all')) {
                      field.onChange(
                        currentResponsibles.length === totalResponsibles
                          ? []
                          : responsiblesData?.map((r: any) => r.id),
                      );
                    } else field.onChange(val);
                  }}
                  input={<OutlinedInput label="Responsáveis" />}
                  renderValue={(selected) =>
                    responsiblesData
                      ?.filter((r: any) => selected.includes(r.id))
                      .map((r: any) => r.name)
                      .join(', ')
                  }
                  MenuProps={MenuProps}
                >
                  <MenuItem value="all">
                    <Checkbox
                      checked={
                        (responsiblesData?.length ?? 0) > 0 &&
                        currentResponsibles.length === (responsiblesData?.length ?? 0)
                      }
                      indeterminate={
                        currentResponsibles.length > 0 &&
                        currentResponsibles.length < (responsiblesData?.length ?? 0)
                      }
                    />
                    <ListItemText primary="Selecionar Todos" />
                  </MenuItem>
                  <Divider />
                  {responsiblesData?.map((item: any) => (
                    <MenuItem key={item.id} value={item.id}>
                      <Checkbox checked={field.value.includes(item.id)} />
                      <ListItemText primary={item.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </Grid2>

        <Grid2 size={6}>
          <Controller
            name="initialDate"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <DatePickerComponent
                value={field.value ? dayjs(field.value).utc() : null}
                onChange={(val) =>
                  field.onChange(
                    dayjs(val).isValid()
                      ? dayjs(val).utc().toISOString()
                      : null,
                  )
                }
                errorMessage={error?.message}
                label="Início"
                disablePast
              />
            )}
          />
        </Grid2>

        <Grid2 size={6}>
          <Controller
            name="validUntil"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <DatePickerComponent
                value={field.value ? dayjs(field.value).utc() : null}
                onChange={(val) =>
                  field.onChange(
                    dayjs(val).isValid()
                      ? dayjs(val).utc().toISOString()
                      : null,
                  )
                }
                errorMessage={error?.message}
                label="Fim"
                disablePast
              />
            )}
          />
        </Grid2>

        <Grid2 size={12}>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextAreaComponentV2
                label="Mensagem"
                value={transformPlainTextToHtml(field.value || '')}
                onChangeVal={field.onChange}
              />
            )}
          />
        </Grid2>

        <Grid2 size={12}>
          <ButtonComponent
            variant="contained"
            fullWidth
            loading={isLoadingCreateSchoolBoard || isLoadingUpdateSchoolBoard}
            onClick={handleSubmit(handleSave)}
          >
            Salvar Recado
          </ButtonComponent>
        </Grid2>
      </Grid2>
    </ModalComponent>
  );
};

export default SchoolBoardEditModal;